// Disabling camelcase for this file due to strict contract with tracking services in place. This should be less of an issue in the future as we move of of rails.
/* eslint-disable camelcase */
import { useEffect, useRef } from 'react';
import {
  ga4Track,
  coreDataInitializedEvent,
  viewSearchResultsEvent,
} from '@unisporkal/ga4';
import {
  useGallery,
  useIsPanelOpen,
  useAssetsLayout,
  usePage,
  useSite,
  useSpectrum,
} from 'app/hooks';
import useRawAppliedFiltersAdapter from 'app/adapters/shared/AppliedFiltersAdapter/useRawAppliedFiltersAdapter';
import {
  getPaAgreementType,
  prepareTrackingData,
  getSearchType,
  getSubscopeName,
  getSearchCategory,
} from 'app/support/tracking';

export default () => {
  const appliedFilters = useRawAppliedFiltersAdapter();
  const gallery = useGallery();
  const page = usePage();
  const site = useSite();
  const spectrumTracking = useSpectrum();
  const isPanelOpen = useIsPanelOpen();
  const layout = useAssetsLayout();
  const pageNameRef = useRef();
  const pageName =
    typeof window === 'undefined' ? null : window.tracking_data?.page_name;

  const creativeAssetsType =
    gallery.searchParameters.mediatype || (gallery.isCreative() ? 'all' : null);

  // This matches the format used in tracking.rb for the search_filter_toggle_state parameter.
  const searchFilterToggleState = (searchQuery) => {
    const analogOnly = searchQuery.offlinecontent === 'exclusive';
    const excludeEditorialUseOnly = site.isGetty()
      ? searchQuery.family?.includes('creative')
      : searchQuery.alloweduse === 'availableforalluses';
    const marketFreezeReady =
      searchQuery.licenseexclusivity === 'exclusiveonly';

    const searchFilterToggleObj = {
      exclude_editorial_use_only: excludeEditorialUseOnly,
      embeddable_images: searchQuery.embeddable,
      exclude_nudity: searchQuery.excludenudity,
      analog_only: analogOnly,
      market_freeze_ready: marketFreezeReady,
    };

    // 85zrz2up8 - This won't work since it seems that agreements are not populating in searchQuery javascript.
    // The populate correctly in search/app/models/tracking.rb
    if (site.isGetty()) {
      searchFilterToggleObj.easy_access =
        searchQuery.agreements?.includes('ea:');
      searchFilterToggleObj.editorial_subscription =
        searchQuery.agreements?.includes('ed:');
      searchFilterToggleObj.premium_access =
        searchQuery.agreements?.includes('pa:');
    }

    const searchFilterToggle = Object.keys(searchFilterToggleObj).map(
      (key) => `${String(key)}:${searchFilterToggleObj[key] ? 'on' : 'off'}`
    );

    return searchFilterToggle.toString();
  };

  const ga4TrackViewSearchResults = () => {
    const uiElementLocation = window?.giLocalStorage.getItem(
      'tracking-data.ui-element-location'
    );
    const {
      page_name,
      search_query,
      search_suggestions_shown,
      search_result_display_options,
    } = window.tracking_data || {};
    const ga4Event = viewSearchResultsEvent({
      asset_type: search_query.assettype,
      gallery_layout: layout === 'mosaic' ? 'mosaic' : 'grid',
      search_method: getSearchType(gallery),
      search_media_type: getSubscopeName(gallery) || undefined,
      search_term: gallery.searchParameters.phrase,
      search_category: getSearchCategory(gallery),
      search_sub_category: search_query.editorialproducts || undefined,
      search_result_count: gallery.totalResults,
      search_filter_asset_orientation: search_query.orientations,
      search_filter_toggle_state: searchFilterToggleState(search_query),
      istock_collection: search_query.istockcollection || undefined,
      search_sort_order: search_query.sort,
      search_license_models: search_query.license.length
        ? search_query.license.join(',')
        : undefined,
      search_suggestions_shown: search_suggestions_shown.length
        ? search_suggestions_shown
        : undefined,
      search_result_display_option: search_result_display_options,
      search_results_page_depth: gallery.pageNumber(),
      search_concepts: search_query.number_of_concepts || undefined,
      ui_element_location: uiElementLocation || undefined,
      page_name,
    });

    ga4Track(ga4Event);
  };

  const trackPageName = (previousPageName) => {
    if (typeof window === 'undefined') return;

    window?.localStorage.setItem(
      'previous_page_name',
      window.tracking_data.page_name
    );

    const event = coreDataInitializedEvent({
      page_name: window.tracking_data.page_name,
      previous_page_name: previousPageName,
      seo_redirect_page: window.tracking_data.seo_redirect_page,
      admin_id: window.tracking_data.admin_id || undefined,
    });

    ga4Track(event);
  };

  useEffect(() => {
    // Tracking not required on page load
    if (pageNameRef.current) {
      trackPageName(pageNameRef.current);
    }

    pageNameRef.current = pageName;
  }, [pageName]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (page.isInitializedOrLoaded()) {
      // eslint-disable-next-line complexity
      setTimeout(() => {
        const event =
          window.giLocalStorage.getItem('tracking-data.event') || 'search';

        // gtm
        prepareTrackingData(gallery, site, appliedFilters, isPanelOpen, layout);
        // siteCatalyst needs to be refreshed with new tracking data
        document.dispatchEvent(new Event('searchEventTracked'));

        if (
          event === 'search' &&
          !(
            gallery.searchParameters.events &&
            !window.tracking_data?.referrer?.includes('/detail/')
          )
        ) {
          ga4TrackViewSearchResults();
        }

        // spectrum
        const spectrumSearchData = {
          asset_ids: gallery.assets.map((asset) => asset.id),
          asset_family: gallery.searchParameters.family,
          creative_assets_type: creativeAssetsType,
          exclude_nudity: gallery.searchParameters.excludenudity,
          license: gallery.searchParameters.license,
          number_of_concepts: gallery.queriedKeywordsByClause?.length,
          page_number: gallery.pageNumber(),
          pa_agreement_filter: getPaAgreementType(),
          search_page_format: gallery.renderGatewayView
            ? 'gateway'
            : 'traditional',
          sort_type: gallery.searchParameters.sortorder,
          // deprecated
          phrase: gallery.searchParameters.phrase,
          queried_keywords_by_clause: gallery.queriedKeywordsByClause,
          total_number_of_results: gallery.totalResults,
          // reevaluating need for field
          sbi: window.tracking_data?.gallery_data.sbi
            ? window.tracking_data?.gallery_data.sbi
            : undefined,
        };
        spectrumTracking.sendSearchSignal(spectrumSearchData);
      });
    }
  }, [page.status]);
};
